/**
 * #1017 — the rules for editing a resting Polymarket US order.
 *
 * US has no replace route, so an edit is cancel then place. A cancel
 * acknowledgement is not a canceled order (docs/260927PolymarketUsCancellation.md):
 * only the order's own lifecycle, read back after the cancel, can say the
 * original stopped resting. The replacement may be placed only then, and only
 * when no fill landed during the edit, so a trader can never hold both orders
 * or more exposure than they chose.
 */
import type { UsAccountContext } from './accountClient';
import { isSameUsScope, usCancelView } from './portfolioModel';
import { decimalUnits, durationPhrase, durationShort, formatUnits, GTD, utcExpiry, type UsTicketEntry } from './ticketModel';
import type { UsCancelResult, UsOrderRow } from './types';

const GTC = 'TIME_IN_FORCE_GOOD_TILL_CANCEL';

/** The original order's terms; null where its row did not carry them. */
type EditTerms = { timeInForce: string | null; postOnly: boolean | null; goodTillTime: string | null };

/** An expiry as exact ISO UTC ("2026-10-08T00:00:00.000Z"); null when unreadable. */
function isoOf(value: string | null | undefined): string | null {
  const ms = value ? Date.parse(value) : NaN;
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

function termsOf(order: UsOrderRow): EditTerms {
  const timeInForce = order.timeInForce ?? null;
  return { timeInForce, postOnly: order.postOnly ?? null,
    goodTillTime: timeInForce === GTD ? isoOf(order.goodTillTime) : null };
}

/** An exact ISO UTC expiry as the ticket's UTC field text ("2026-10-08T00:00:00"). */
function expiryFieldOf(iso: string): string {
  return /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d+)?Z$/.exec(iso)?.[1] ?? '';
}

/**
 * The replacement starts as the order's remaining size at its price, with the
 * order's own duration, expiry and post-only setting, so terms never change
 * unless the trader changes them. A term the row does not carry starts at the
 * safer setting, post-only and good till canceled, and the ticket says so.
 */
export function editEntryFrom(order: UsOrderRow): UsTicketEntry {
  const terms = termsOf(order);
  return {
    outcome: order.target.outcome,
    action: order.action,
    quantity: remainingOf(order),
    outcomePrice: order.outcomePrice,
    timeInForce: terms.timeInForce ?? GTC,
    expiry: terms.goodTillTime ? expiryFieldOf(terms.goodTillTime) : '',
    postOnly: terms.postOnly ?? true,
  };
}

/** The original's known terms for the edit header: "post-only · good till 10/08 00:00 UTC". */
export function editTermsHeader(order: UsOrderRow): string {
  const terms = termsOf(order);
  return [
    ...(terms.postOnly === null ? [] : [terms.postOnly ? 'post-only' : 'not post-only']),
    ...(terms.timeInForce === null ? [] : [durationPhrase(terms.timeInForce, terms.goodTillTime)]),
  ].join(' · ');
}

/** Which original terms are unknown and what the replacement starts with; null when all are known. */
export function editUnknownTerms(order: UsOrderRow): string | null {
  const terms = termsOf(order);
  if (terms.postOnly === null && terms.timeInForce === null) {
    return 'Original post-only and duration unknown; the replacement starts post-only, good till canceled.';
  }
  if (terms.postOnly === null) return 'Original post-only unknown; the replacement starts post-only.';
  if (terms.timeInForce === null) return 'Original duration unknown; the replacement starts good till canceled.';
  if (terms.timeInForce === GTD && terms.goodTillTime === null) return 'Original expiry unknown; set one before replacing.';
  return null;
}

/** One field's note against the original: amber (`warn`) when it differs or is unknown. */
export type UsEditTermNote = { text: string; warn: boolean };

/** The exact expiry an entry would send, or null. */
const entryExpiry = (entry: UsTicketEntry) => (entry.timeInForce === GTD ? utcExpiry(entry.expiry) : null);

/** How the entry's duration and post-only compare with the original's. */
export function editTermNotes(entry: UsTicketEntry, order: UsOrderRow): { duration: UsEditTermNote; postOnly: UsEditTermNote } {
  const terms = termsOf(order);
  const same: UsEditTermNote = { text: 'As original', warn: false };
  const unknown: UsEditTermNote = { text: 'Original unknown', warn: true };
  const durationChanged = terms.timeInForce !== null && (entry.timeInForce !== terms.timeInForce
    || (terms.timeInForce === GTD && entryExpiry(entry) !== terms.goodTillTime));
  return {
    duration: terms.timeInForce === null ? unknown : durationChanged
      ? { text: `Changed: original was ${durationShort(terms.timeInForce, terms.goodTillTime)}`, warn: true } : same,
    postOnly: terms.postOnly === null ? unknown : entry.postOnly !== terms.postOnly
      ? { text: `Changed: original was ${terms.postOnly ? 'post-only' : 'not post-only'}`, warn: true } : same,
  };
}

/** The post-only field's quick reference on an edit. */
export function editPostOnlyHelp(order: UsOrderRow): string {
  const postOnly = termsOf(order).postOnly;
  if (postOnly === null) return 'Original unknown; starts on. Off lets the replacement take liquidity and pay taker fees.';
  return postOnly ? 'Original: on. Off lets the replacement take liquidity and pay taker fees.'
    : 'Original: off. On keeps the replacement resting; it never takes liquidity.';
}

function remainingOf(order: UsOrderRow): string {
  const life = order.lifecycle;
  if (!life) return order.quantity;
  if (life.remainingQuantity !== null) return life.remainingQuantity;
  const total = decimalUnits(order.quantity), filled = decimalUnits(life.filledQuantity);
  return total !== null && filled !== null && filled <= total ? formatUnits(total - filled) : order.quantity;
}

/** Why this order cannot be edited from this ticket, or null when it can. */
export function editBlocker(order: UsOrderRow, context: UsAccountContext, nowMs: number): string | null {
  if (!isSameUsScope(order.scope, context.scope)) return 'This order belongs to a different account.';
  // Native replacement (cancel + new) is not available; a single cancel is.
  if (order.protocol === 'institutional') return 'Native orders cannot be replaced yet. Cancel it and place a new order.';
  const view = usCancelView(order, context.readiness, nowMs);
  if (view.canCancel) return null;
  return view.blockedReason ?? view.status ?? 'This order can no longer be canceled.';
}

/**
 * True when the entry would replace the order with the same price, size and
 * terms (duration, expiry, post-only) it starts from: a change of terms alone
 * is a change. An unknown original term counts as the setting it starts at.
 */
export function editUnchanged(entry: UsTicketEntry, order: UsOrderRow): boolean {
  const same = (a: string, b: string) => {
    const x = decimalUnits(a.trim()), y = decimalUnits(b);
    return x !== null && x === y;
  };
  const start = editEntryFrom(order);
  return same(entry.quantity, remainingOf(order)) && same(entry.outcomePrice, order.outcomePrice)
    && entry.timeInForce === start.timeInForce && entry.postOnly === start.postOnly
    && entryExpiry(entry) === entryExpiry(start);
}

/** What a cancel, or a later status check, established about the original order. */
export type UsEditSettlement =
  /** The original no longer rests and no fill landed during the edit: place the replacement. */
  | { state: 'settled' }
  /** The original filled completely before the cancel took effect. */
  | { state: 'filled' }
  /** The original stopped resting, but `newlyFilled` more of it filled during the edit. */
  | { state: 'filled_during_edit'; newlyFilled: string }
  /** Nothing was sent: the original order is still live. */
  | { state: 'refused'; code: string }
  /** The cancel may have reached the venue; whether the original still rests is unknown. */
  | { state: 'unconfirmed'; code: string };

/**
 * Client refusals raised before the cancel POST leaves the browser
 * (portfolioClient `cancel` / `setSelection`). Any other failure, including a
 * failed read after the POST or the relay's 503 (which also carries its own
 * permission refusals), may have reached the venue and is unconfirmed.
 */
const CANCEL_NOT_SENT = new Set([
  'US_CLIENT_INVALID_REQUEST', 'US_CLIENT_ORDER_CHANGED', 'US_CLIENT_CANCEL_PENDING',
  'US_CLIENT_INVALID_SCOPE', 'US_CLIENT_AUTH_REQUIRED',
]);

/** A thrown cancel failure. */
export function editCancelFailure(code: string): UsEditSettlement {
  return CANCEL_NOT_SENT.has(code) ? { state: 'refused', code } : { state: 'unconfirmed', code };
}

/** The cancel call's answer, judged against the order as the edit started. */
export function editCancelSettlement(result: UsCancelResult, original: UsOrderRow): UsEditSettlement {
  if (result.intentId !== original.intentId) return { state: 'unconfirmed', code: 'US_CANCEL_TARGET_MISMATCH' };
  if (result.attempt.state === 'not_sent') return { state: 'refused', code: 'US_CANCEL_NOT_SENT' };
  if (result.readback !== 'confirmed' || !result.order) return { state: 'unconfirmed', code: 'US_CANCEL_READBACK_UNAVAILABLE' };
  return editOrderSettlement(result.order, original);
}

/**
 * A fresh read of the order after a cancel was sent. `canceled` and `expired`
 * both mean it no longer rests; anything else keeps the edit unconfirmed.
 */
export function editOrderSettlement(order: UsOrderRow, original: UsOrderRow): UsEditSettlement {
  if (order.intentId !== original.intentId || order.orderId !== original.orderId) {
    return { state: 'unconfirmed', code: 'US_CANCEL_TARGET_MISMATCH' };
  }
  const life = order.lifecycle;
  if (!life) return { state: 'unconfirmed', code: 'US_ORDER_NOT_OBSERVED' };
  if (life.state === 'filled') return { state: 'filled' };
  if (life.state !== 'canceled' && life.state !== 'expired') {
    return { state: 'unconfirmed', code: `US_ORDER_${life.state.toUpperCase()}` };
  }
  const before = decimalUnits(original.lifecycle?.filledQuantity ?? '0');
  const after = decimalUnits(life.filledQuantity);
  if (before === null || after === null) return { state: 'unconfirmed', code: 'US_ORDER_FILLS_UNREADABLE' };
  if (after < before) return { state: 'unconfirmed', code: 'US_ORDER_FILLS_REGRESSED' };
  return after > before ? { state: 'filled_during_edit', newlyFilled: formatUnits(after - before) } : { state: 'settled' };
}
